import { useEffect, useRef, useState, useCallback } from "react";
import "./FindCareer.scss";
import { ArrowIcon } from "../../components/icons";
import CareerSaveButton from "../../components/CareerSaveButton/CareerSaveButton";
import { useDispatch, useSelector } from "react-redux";
import {
  getCareersRequest,
  setCareersRequest,
} from "../../redux/actions/careerActions";
import { initCompanions } from "../../redux/actions";

export default function FindCareer({ companion_id }) {
  const [openAccordian, setOpenAccordian] = useState([]);
  const [recentlySavedIds, setRecentlySavedIds] = useState([]);
  const [query, setQuery] = useState("");
  const [companionCareerId, setCompanionId] = useState("");
  const { careers, saveId, isLoadingSaveCareer } = useSelector(
    (state) => state.careersReducer,
  );
  const loading = useSelector((state) => state.careersReducer.loading);
  const companionId = useSelector(
    (state) => state.aiCompanionsReducer.aiAgents.results?.[0]?.id,
  );
  useEffect(() => {
    if (companionId) {
      setCompanionId(companionId);
    }
  }, [companionId]);
  
  useEffect(() => {
    if (isLoadingSaveCareer) {
      setRecentlySavedIds((prev) => [...prev, saveId.career_key]);
      setTimeout(() => {
        setRecentlySavedIds((prev) =>
          prev.filter((id) => id !== saveId.career_key),
        );
      }, 3000);
    }
  }, [saveId, isLoadingSaveCareer]);

  const dispatch = useDispatch();

  const list = careers?.careers || [];
  const hasMore = careers?.has_more || false;
  const nextOffset = careers?.next_offset || 0;

  const loadMoreRef = useRef(null);
  const observerRef = useRef(null);

  const toggleAccordion = (index) => {
    if (openAccordian.includes(index)) {
      setOpenAccordian(openAccordian.filter((i) => i !== index));
    } else {
      setOpenAccordian([...openAccordian, index]);
    }
  };

  const handleSaveCareer = (career) => {
    dispatch(
      setCareersRequest({
        career_key: career.id,
        interested: !career.interested.added,
        interestedId: career.interested.id,
      }),
    );
  };

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      dispatch(getCareersRequest({ query, offset: nextOffset, limit: 20 }));
    }
  }, [loading, hasMore, nextOffset,query, dispatch]);

  useEffect(() => {
    if (!loadMoreRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => observerRef.current?.disconnect();
  }, [loadMore]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim().length >= 3) {
        setOpenAccordian([]);
        dispatch(getCareersRequest({ query, offset: 0, limit: 20 }));
      }
      if (!query.trim()?.length <= 2) {
        dispatch({ type: "CLEAR_CAREERS" });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [query, dispatch]);

  useEffect(() => {
    if (!companion_id) {
      dispatch(initCompanions({ name__iexact: "Career Buddy" }));
    }
  }, [dispatch, companion_id]);

  return (
    <>
      <h3>Find a Career</h3>
      <div className="Find__career__input__wrapper">
        <input
          type="text"
          className="Find__career__container__input"
          placeholder="Search careers (e.g., nurse, electrician, game designer)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {loading && !nextOffset && <span className="career-save-button__loader" />}
      </div>
      <div className="accordion">
        {!query.trim() ? (
          <div className="no-career-text">
            No careers found. Try searching with different keywords.
          </div>
        ) : list.length === 0 ? (
          <div className="no-career-text">No items matched your search.</div>
        ) : (
          <>
            {list.map((item, index) => {
              // const isSaved = savedIds.includes(item.id);
              const isSaved = item.interested.added;
              const isRecentlySaved = recentlySavedIds.includes(item.id);
              return (
                <div key={item.id ?? index} className="accordion__item">
                  <div
                    className="accordion__header"
                    onClick={() => toggleAccordion(index)}
                  >
                    <p>{item.mask_title}</p>
                    <span
                      className={`arrow ${
                        openAccordian.includes(index) ? "open" : ""
                      }`}
                    >
                      <ArrowIcon />
                    </span>
                  </div>

                  {openAccordian.includes(index) && (
                    <div className="accordion__body open">
                      <div className="accordion__content">
                        <p>{item.description}</p>
                        {!item.interested.added ? (
                          <CareerSaveButton
                            text={
                              isLoadingSaveCareer && isRecentlySaved
                                ? "Saved to Your Careers!"
                                : isRecentlySaved
                                  ? "Unsaved this Career"
                                  : "Save this Career"
                            }
                            variant={isRecentlySaved ? "outline" : "filled"} // ← drives the 3s flash
                            isSaved={isSaved}
                            handleSaveCareer={() => handleSaveCareer(item)}
                          />
                        ) : (
                          <CareerSaveButton
                            text={
                              isLoadingSaveCareer && isRecentlySaved
                                ? "Unsaved this Career"
                                : isRecentlySaved
                                  ? "Saved to Your Careers!"
                                  : "Unsave this Career"
                            }
                            variant={isRecentlySaved ? "outline" : "filled"} // ← drives the 3s flash
                            isSaved={isSaved}
                            handleSaveCareer={() => handleSaveCareer(item)}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            <div ref={loadMoreRef} style={{ height: 1 }} />
            {loading && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "12px",
                }}
              >
                <span className="career-save-button__loader"></span>
              </div>
            )}
          </>
        )}
      </div>
      <div className="link-text">
        <a
          href={`/profile/ai-companions/${companionCareerId}/career-buddy`}
          target="_blank"
          rel="noopener noreferrer"
          className="Find__career__container__link"
        >
          Not sure where to start? Ask Career Buddy for ideas.
        </a>
      </div>
    </>
  );
}
